/*©**********************************************************************************
*                                                                                   *
* SPDX-License-Identifier: AGPL-3.0-or-later                                        *
*                                                                                   *
* This program is licensed under the GNU Affero General Public License v3+.         *
* See the LICENSE file or <https://www.gnu.org/licenses/agpl-3.0.html> for details. *
*                                                                                   *
**********************************************************************************©*/

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Logo from '../assets/image/logo.svg';
import UserOne from '../assets/image/user-10.png';
import { endAuthSession } from '../utils/auth';
import { showNotification } from '@mantine/notifications';
import { getMediaUrl } from '../utils/media';
import { apiClient } from '../utils/apiClient';
import { getAppName } from '../config/app';

interface MenuItem {
  icon: string;
  label: string;
  href: string;
  subItems?: MenuItem[];
}

interface SidebarProps {
  menuItems: MenuItem[];
}

const Sidebar: React.FC<SidebarProps> = ({ menuItems }) => {
  const appName = getAppName();
  const location = useLocation();
  const navigate = useNavigate();
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<{ name?: string; email?: string } | null>(null);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState<boolean>(false);

  useEffect(() => {
    const loadFromStorage = () => {
      const storedUser = localStorage.getItem('user');
      if (!storedUser) return;
      try {
        const user = JSON.parse(storedUser);
        setUserInfo({ name: user.name || 'User', email: user.email || '' });
        setAvatarUrl(user.avatar_url || null);
        const raw = user?.is_admin ?? user?.isAdmin;
        setIsAdmin(raw === true || raw === 1);
      } catch (error) {
        console.error('Error parsing user info:', error);
      }
    };

    const loadFromProfile = async () => {
      try {
        const profile = await apiClient.get('/profile/me');
        if (profile) {
          setUserInfo({ name: profile.name || 'User', email: profile.email || '' });
          if (profile.avatar_url) {
            setAvatarUrl(profile.avatar_url);
          }

          const storedUserRaw = localStorage.getItem('user');
          if (storedUserRaw) {
            try {
              const storedUser = JSON.parse(storedUserRaw);
              storedUser.avatar_url = profile.avatar_url || storedUser.avatar_url;
              storedUser.name = profile.name || storedUser.name;
              storedUser.email = profile.email || storedUser.email;
              localStorage.setItem('user', JSON.stringify(storedUser));
            } catch {
              // ignore
            }
          }

          const raw = (profile as any)?.is_admin ?? (profile as any)?.isAdmin;
          if (raw !== undefined) {
            setIsAdmin(raw === true || raw === 1);
          }
        }
      } catch (error) {
        // best-effort only; sidebar still works with localStorage data
        console.error('Failed to load profile for sidebar', error);
      }
    };

    loadFromStorage();
    loadFromProfile();

    const handleUserUpdated = () => {
      loadFromStorage();
    };

    window.addEventListener('user-updated', handleUserUpdated);
    return () => window.removeEventListener('user-updated', handleUserUpdated);
  }, []);

  const handleLogout = () => {
    endAuthSession();
    showNotification({
      title: 'Logged Out',
      message: 'You have been logged out successfully',
      color: 'blue',
    });
    navigate('/');
  };

  const toggleDropdown = (label: string) => {
    setOpenDropdown(openDropdown === label ? null : label);
  };

  const isActive = (href: string) => {
    if (href === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(href);
  };

  const renderMenuItem = (item: MenuItem) => {
    const active = isActive(item.href);
    
    if (item.subItems) {
      const hasActiveSubItem = item.subItems.some(subItem => isActive(subItem.href));
      const isOpen = openDropdown === item.label || hasActiveSubItem;

      return (
        <div key={item.label}>
          <button
            onClick={() => toggleDropdown(item.label)}
            className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
              active || isOpen
                ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
                : 'text-text-light-secondary dark:text-text-dark-secondary hover:bg-background-light dark:hover:bg-background-dark'
            }`}
          >
            <span className="material-symbols-outlined text-2xl">{item.icon}</span>
            <p className="flex-1 text-left">{item.label}</p>
            <span className="material-symbols-outlined text-lg">
              {isOpen ? 'expand_less' : 'expand_more'}
            </span>
          </button>
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2 }}
                className="ml-6 mt-1 overflow-hidden"
              >
                <div className="space-y-1 border-l border-border-light dark:border-border-dark pl-4">
                  {item.subItems.map((subItem) => {
                    const subActive = isActive(subItem.href);
                    return (
                      <Link
                      key={subItem.label}
                        to={subItem.href}
                        className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                          subActive
                            ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary font-semibold'
                            : 'text-text-light-secondary dark:text-text-dark-secondary hover:bg-background-light dark:hover:bg-background-dark'
                        }`}
                      >
                        <span className="material-symbols-outlined text-xl">{subItem.icon}</span>
                        <p>{subItem.label}</p>
                      </Link>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      );
    }

    return (
      <div key={item.label}>
        <Link
          to={item.href}
          className={`flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
            active
              ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
              : 'text-text-light-secondary dark:text-text-dark-secondary hover:bg-background-light dark:hover:bg-background-dark'
          }`}
        >
          <span className="material-symbols-outlined text-2xl">{item.icon}</span>
          <p>{item.label}</p>
        </Link>
      </div>
    );
  };

  return (
    <aside className="flex h-full w-64 flex-shrink-0 flex-col bg-card-light dark:bg-card-dark border-r border-border-light dark:border-border-dark p-6">
      <div className="flex h-full flex-col">
        {/* Logo Section */}
        <div className="mb-8 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3">
            <img src={Logo} alt={appName} className="h-10 w-10" />
            <div className="flex flex-col">
              <h1 className="text-text-light-primary dark:text-text-dark-primary text-lg font-bold leading-tight">
                {appName}
              </h1>
              <p className="text-text-light-secondary dark:text-text-dark-secondary text-xs font-normal leading-tight">
                Trading Dashboard
              </p>
            </div>
          </Link>
        </div>

        {/* Main Navigation – visible for all users (including admins) */}
        <>
          <div className="mb-4">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-text-light-secondary dark:text-text-dark-secondary">
              Navigation
            </h2>
          </div>
          <nav className="flex flex-1 flex-col gap-1">
            {menuItems.map(renderMenuItem)}
          </nav>
        </>

        {/* Admin Navigation (visible only for admins) */}
        {isAdmin && (
          <div className="mt-6">
            <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-text-light-secondary dark:text-text-dark-secondary">
              Admin
            </h2>
            <nav className="flex flex-col gap-1">
              <div key="Admin">
                <button
                  onClick={() => toggleDropdown('Admin')}
                  className={`group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                    location.pathname.startsWith('/admin')
                      ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary'
                      : 'text-text-light-secondary dark:text-text-dark-secondary hover:bg-background-light dark:hover:bg-background-dark'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl">admin_panel_settings</span>
                  <p className="flex-1 text-left">Admin Panel</p>
                  <span className="material-symbols-outlined text-lg">
                    {openDropdown === 'Admin' || location.pathname.startsWith('/admin') ? 'expand_less' : 'expand_more'}
                  </span>
                </button>
                <AnimatePresence>
                  {(openDropdown === 'Admin' || location.pathname.startsWith('/admin')) && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.2 }}
                      className="ml-6 mt-1 overflow-hidden"
                    >
                      <div className="space-y-1 border-l border-border-light dark:border-border-dark pl-4">
                        <Link
                          to="/admin"
                          className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                            location.pathname === '/admin'
                              ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary font-semibold'
                              : 'text-text-light-secondary dark:text-text-dark-secondary hover:bg-background-light dark:hover:bg-background-dark'
                          }`}
                        >
                          <span className="material-symbols-outlined text-xl">dashboard</span>
                          <p>Dashboard</p>
                        </Link>
                        <Link
                          to="/admin/signup-requests"
                          className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                            location.pathname === '/admin/signup-requests'
                              ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary font-semibold'
                              : 'text-text-light-secondary dark:text-text-dark-secondary hover:bg-background-light dark:hover:bg-background-dark'
                          }`}
                        >
                          <span className="material-symbols-outlined text-xl">how_to_reg</span>
                          <p>Signup Requests</p>
                        </Link>
                        <Link
                          to="/admin/members"
                          className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                            location.pathname === '/admin/members'
                              ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary font-semibold'
                              : 'text-text-light-secondary dark:text-text-dark-secondary hover:bg-background-light dark:hover:bg-background-dark'
                          }`}
                        >
                          <span className="material-symbols-outlined text-xl">group</span>
                          <p>Members</p>
                        </Link>
                        <Link
                          to="/admin/swap-queue"
                          className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                            location.pathname === '/admin/swap-queue'
                              ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary font-semibold'
                              : 'text-text-light-secondary dark:text-text-dark-secondary hover:bg-background-light dark:hover:bg-background-dark'
                          }`}
                        >
                          <span className="material-symbols-outlined text-xl">queue</span>
                          <p>Swap Queue</p>
                        </Link>
                        <Link
                          to="/admin/settings/global"
                          className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors ${
                            location.pathname === '/admin/settings/global'
                              ? 'bg-primary/10 text-primary dark:bg-primary/20 dark:text-primary font-semibold'
                              : 'text-text-light-secondary dark:text-text-dark-secondary hover:bg-background-light dark:hover:bg-background-dark'
                          }`}
                        >
                          <span className="material-symbols-outlined text-xl">tune</span>
                          <p>Global Settings</p>
                        </Link>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </nav>
          </div>
        )}
      </div>

        {/* Bottom Section */}
      <div className="flex flex-col gap-2 mt-auto">
        <Link
          to="/help"
          className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-text-light-secondary transition-colors hover:bg-background-light dark:text-text-dark-secondary dark:hover:bg-background-dark"
        >
          <span className="material-symbols-outlined text-2xl">help</span>
          <p>Support</p>
        </Link>
        <div className="my-2 h-px w-full bg-border-light dark:bg-border-dark"></div>
        <div className="flex items-center gap-3 rounded-lg px-3 py-2 transition-colors hover:bg-background-light dark:hover:bg-background-dark">
          <div className="flex-shrink-0 h-10 w-10 rounded-full border-2 border-border-light dark:border-border-dark overflow-hidden">
            <img
              src={getMediaUrl(avatarUrl) || UserOne}
              alt={userInfo?.name || 'User'}
              className="h-full w-full object-cover"
            />
          </div>
          <div className="flex flex-1 flex-col">
            <p className="text-sm font-semibold leading-normal text-text-light-primary dark:text-text-dark-primary">
              {userInfo?.name || 'User'}
            </p>
            <p className="text-xs font-normal leading-normal text-text-light-secondary dark:text-text-dark-secondary">
              {userInfo?.email || 'No email'}
            </p>
          </div>
          <button
            onClick={handleLogout}
            className="text-text-light-secondary transition-colors hover:text-primary dark:text-text-dark-secondary dark:hover:text-primary"
            aria-label="Logout"
          >
            <span className="material-symbols-outlined text-xl">exit_to_app</span>
          </button>
        </div>
      </div>
    </aside>
  );
};

export default Sidebar;
